/**
 * Buy a flex pack.
 *
 * The whole surface is a list of packs and a button each. Pressing one asks
 * the backend for a Stripe Checkout session and leaves for Stripe, which
 * collects the card, runs 3-D Secure and offers the PROMO CODE box. Nothing
 * here touches a card number, and nothing here decides a price: the client
 * sends a pack CODE and the backend resolves it to the Stripe price it owns.
 *
 * WHAT MAKES A PACK WORTH BUYING is that it never expires, so that is said
 * once at the top and again on every card rather than being left in a
 * footnote. A firm choosing between a pack and a bigger plan is choosing
 * between "spend it whenever" and "use it or lose it".
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  PiArrowSquareOutLight,
  PiInfinityLight,
  PiLightningLight,
  PiShieldCheckLight,
  PiTagLight,
} from 'react-icons/pi';
import { useAppDispatch } from '../../../store';
import {
  PackSku,
  UnitBalance,
  createPackCheckout,
  fetchPackCatalogue,
  fetchUnitBalances,
} from '../../../store/actions/PackActions';
import { formatEur } from '../../../config/pricing';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import GlassCard from '../brand/GlassCard';
import Button from '../TailwindComponents/Button';
import Badge from '../TailwindComponents/Badge';
import Skeleton from '../TailwindComponents/Skeleton';

/** Where Stripe sends the customer back to. Absolute, as Stripe requires. */
const returnUrls = () => ({
  success_url: `${window.location.origin}/packs/complete?status=success&session_id={CHECKOUT_SESSION_ID}`,
  cancel_url: `${window.location.origin}/packs?status=cancelled`,
});

interface PackCardProps {
  pack: PackSku;
  balance?: UnitBalance;
  busy: boolean;
  disabled: boolean;
  onBuy: (pack: PackSku) => void;
}

const PackCard: React.FC<PackCardProps> = ({ pack, balance, busy, disabled, onBuy }) => {
  const { t } = useTranslation();
  return (
    <GlassCard
      className={pack.featured ? 'border-champagne/50' : undefined}
      data-testid={`pack-card-${pack.code}`}
    >
      <div className="flex flex-col h-full gap-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-fg-strong">{t(pack.name)}</p>
            <p className="mt-0.5 text-xs text-fg-muted leading-relaxed">
              {t(pack.description)}
            </p>
          </div>
          {pack.featured && (
            <Badge color="yellow" className="flex-shrink-0">{t('Popular')}</Badge>
          )}
        </div>

        {/* The number people are actually buying, and what it costs. */}
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-medium text-fg-strong tabular-nums">
            {pack.units.toLocaleString()}
          </span>
          <span className="text-xs text-fg-muted">{t(pack.unitLabel)}</span>
        </div>

        <div className="flex items-center gap-1.5 text-[11px] text-fg-muted">
          <PiInfinityLight className="w-3.5 h-3.5 text-champagne" aria-hidden />
          {t('Never expires')}
        </div>

        {/* Only when the backend knows - a "0 left" invented on the client
            reads as a limit the firm has hit. */}
        {balance && (
          <p className="text-[11px] text-fg-muted" data-testid={`pack-balance-${pack.code}`}>
            {t('{{count}} left in your balance', { count: balance.pack_balance })}
          </p>
        )}

        <div className="mt-auto pt-2 flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-fg-strong tabular-nums">
            {formatEur(pack.price)}
          </span>
          <Button
            variant="primary"
            size="small"
            isLoading={busy}
            disabled={disabled || pack.purchasable === false}
            onClick={() => onBuy(pack)}
            data-testid={`pack-buy-${pack.code}`}
          >
            {t('Buy')}
          </Button>
        </div>
      </div>
    </GlassCard>
  );
};

const PacksPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const [packs, setPacks] = useState<PackSku[] | null>(null);
  const [balances, setBalances] = useState<UnitBalance[]>([]);
  const [buying, setBuying] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [catalogue, summary] = await Promise.all([
        dispatch(fetchPackCatalogue()),
        dispatch(fetchUnitBalances() as any),
      ]);
      if (cancelled) return;
      setPacks(catalogue);
      setBalances((summary as any)?.balances || []);
    })();
    return () => { cancelled = true; };
  }, [dispatch]);

  const balanceFor = useMemo(() => {
    const byUnit = new Map(balances.map((row) => [row.unit_type, row]));
    return (pack: PackSku) =>
      byUnit.get(pack.code) ||
      byUnit.get(pack.code.replace(/^flex_/, '').replace(/_\d+$/, ''));
  }, [balances]);

  /**
   * Leave for Stripe.
   *
   * `window.location.assign` rather than a new tab: Checkout is the rest of
   * this task, not a side trip, and a popup here is the thing browsers block.
   * The button stays in its loading state until the navigation happens, so a
   * slow session request cannot be double-clicked into two sessions.
   */
  const buy = useCallback(async (pack: PackSku) => {
    if (buying) return;
    setBuying(pack.code);
    const session = await dispatch(createPackCheckout({
      pack_code: pack.code,
      quantity: 1,
      ...returnUrls(),
    }));
    if (session?.url) {
      window.location.assign(session.url);
      return;
    }
    setBuying(null);
    dispatch(showFeedbackModal({
      showModal: true,
      modalType: 'error',
      message: t('That purchase could not be started.'),
      subMsg: t('Nothing has been charged. Please try again in a moment.'),
      duration: 5000,
    }));
  }, [buying, dispatch, t]);

  return (
    <div className="h-full overflow-y-auto custom-scrollbar-main bg-bg">
      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        <header className="space-y-2">
          <h1 className="text-xl font-medium text-fg-strong">{t('Flex packs')}</h1>
          <p className="text-sm text-fg-muted max-w-2xl leading-relaxed">
            {t('Top up whenever you need more, without changing your plan. Packs are prepaid, and they never expire — unused units stay yours.')}
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 pt-1 text-[11px] text-fg-muted">
            <span className="inline-flex items-center gap-1.5">
              <PiInfinityLight className="w-3.5 h-3.5 text-champagne" aria-hidden />
              {t('Never expires')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <PiTagLight className="w-3.5 h-3.5 text-champagne" aria-hidden />
              {t('Promo codes are applied at checkout')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <PiShieldCheckLight className="w-3.5 h-3.5 text-champagne" aria-hidden />
              {t('Card details are handled by Stripe — never by PONS')}
            </span>
          </div>
        </header>

        {!packs ? (
          <Skeleton variant="card" rows={3} inset="none" />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="pack-list">
            {packs.map((pack) => (
              <PackCard
                key={pack.code}
                pack={pack}
                balance={balanceFor(pack)}
                busy={buying === pack.code}
                disabled={!!buying}
                onBuy={buy}
              />
            ))}
          </div>
        )}

        <GlassCard>
          <div className="flex items-start gap-3">
            <PiLightningLight className="w-4 h-4 mt-0.5 text-champagne flex-shrink-0" aria-hidden />
            <div className="min-w-0 space-y-1">
              <p className="text-sm font-medium text-fg-strong">
                {t('Using packs every month?')}
              </p>
              <p className="text-xs text-fg-muted leading-relaxed">
                {t('A module or a bigger plan usually costs less than repeat packs. Compare them on the pricing page.')}
              </p>
              <Button
                variant="outline"
                size="small"
                className="mt-1"
                onClick={() => history.push('/pricing')}
                data-testid="packs-see-pricing"
              >
                <PiArrowSquareOutLight className="w-3.5 h-3.5 me-1.5" aria-hidden />
                {t('See plans and modules')}
              </Button>
            </div>
          </div>
        </GlassCard>
      </div>
    </div>
  );
};

export default PacksPage;
